import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { signIn, requestPasswordReset } from "../lib/authApi";
import { GoogleButton } from "../components/GoogleButton";

const fieldClass =
  "border-white/10 bg-[#121212] text-white placeholder:text-white/30 focus-visible:ring-[#ff2a00]";

const TURNSTILE_SITE_KEY = "0x4AAAAAAEcon4hYVLML-4Mi";
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const widgetId = useRef(null);

  // This Supabase project has captcha protection enabled, so signInWithPassword
  // and the password-reset request both require a Turnstile token. Render the
  // managed widget and capture its token.
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.turnstile || !captchaRef.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(captchaRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => setCaptchaToken(""),
        "expired-callback": () => setCaptchaToken(""),
      });
    };
    const cleanup = () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetId.current !== null) {
          window.turnstile.remove(widgetId.current);
        }
      } catch {
        /* ignore */
      }
      widgetId.current = null;
    };
    if (!document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else if (window.turnstile) {
      render();
    } else {
      const t = setInterval(() => {
        if (window.turnstile) {
          clearInterval(t);
          render();
        }
      }, 200);
      return () => {
        clearInterval(t);
        cleanup();
      };
    }
    return cleanup;
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken("");
    try {
      if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
    } catch {
      /* ignore */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (!captchaToken) {
      setError("Please complete the verification below.");
      return;
    }
    setBusy(true);
    try {
      await signIn(email, password, captchaToken);
      toast.success("Welcome back");
      navigate("/profile");
    } catch (err) {
      setError(err.message);
      resetCaptcha(); // Turnstile tokens are single-use.
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setError("");
    if (!email) {
      setError("Enter your email above, then tap Forgot password.");
      return;
    }
    if (!captchaToken) {
      setError("Please complete the verification below, then tap Forgot password.");
      return;
    }
    try {
      await requestPasswordReset(email, captchaToken);
      toast.success(
        "If that email is registered, a reset link is on its way. It can take a few minutes — remember to check your junk/spam folder."
      );
    } catch {
      toast.success("If that email is registered, a reset link is on its way.");
    } finally {
      resetCaptcha();
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[100dvh] bg-[#050505] px-6 pb-16 pt-6">
      <button
        data-testid="login-back"
        onClick={() => navigate("/")}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0d0d0d] transition-transform duration-200 active:scale-90"
        aria-label="Back to browsing"
      >
        <ChevronLeft className="h-5 w-5 text-white" />
      </button>

      <div className="mx-auto mt-4 max-w-md">
        <Logo />
        <h1 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-white">
          Log in
        </h1>
        <p className="mt-1 text-sm text-white/50">
          Save events and manage your listings.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Email
            </Label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={fieldClass}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Password
            </Label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className={fieldClass}
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-[#ff6a4d]">
              {error}
            </p>
          )}

          <button
            type="button"
            data-testid="login-forgot"
            onClick={forgot}
            className="text-sm font-medium text-white/50 transition-colors hover:text-white"
          >
            Forgot password?
          </button>

          <div data-testid="login-turnstile" ref={captchaRef} className="flex justify-center" />

          <button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
          </button>
        </form>

        <GoogleButton testid="login-google" />

        <p className="mt-6 text-center text-sm text-white/50">
          New to RoadRnr?{" "}
          <Link
            data-testid="login-to-signup"
            to="/signup"
            className="font-semibold text-[#ff2a00]"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
